import { mapGetters, mapState } from 'vuex'
import numberWithCommas from '~/filters/numberWithCommas'
import dayjs from '~/modules/dayjs'
import { capitalizeFirstLetter } from '~/utils/utils'

export default {
  filters: {
    numberWithCommas,
    arrayToStringJoin (value) {
      if (Array.isArray(value)) {
        return value.join(', ')
      } else {
        return [value].join(', ')
      }
    },
    numKeys (object) {
      if (object && object !== undefined) {
        return Object.keys(object).length
      }
      return 0
    },
    rangeFormatter (value) {
      return `${value?.min} - ${value?.max}`
    },
    titlecase (value) {
      return value.replace(/\w\S*/g, txt => txt[0].toUpperCase() + txt.substr(1)?.toLowerCase())
    }
  },
  computed: {
    ...mapState({
      breakpoint: state => state.breakpoint,
      currentCurrency: state => state.lang.currentCurrency,
      bar: state => state.search.bar,
      location: state => state.search.locationFull,
      locationsFull: state => state.search.locationsFull,
      locations: state => state.search.locations,
      pagination: state => state.search.pagination,
      amountResultsFound: state => state.search.amountResultsFound
    }),
    ...mapGetters({
      locale: 'lang/locale',
      pageHead: 'pageHead',
      searchPageState: 'search/searchPageState',
      searchTerms: 'search/searchTerms',
      searchFilters: 'search/filters'
    }),
    loading () {
      return this.$store.state.search.loading || this.page?.loading
    },
    getTopImage () {
      if (!this.location?.media?.length) {
        return
      }
      return this.location.media.find(img => !!img).replace('s2_', 's1_')
    },
    getStartDate () {
      if (!this.$store.state.search.date) {
        return ''
      }
      return dayjs(this.$store.state.search.date).toDate()
    },
    getEndDate () {
      if (!this.$store.state.search.date) {
        return ''
      }
      return dayjs(this.$store.state.search.date).add(this.$store.state.search.duration, 'd').toDate()
    },
    getBoatTypeTitle () {
      let name = ''
      if (this.filters?.boat_types && Array.isArray(this.filters?.boat_types)) {
        name = this.filters?.boat_types?.find(t => this.searchTerms.boat_types.includes(t.key))?.name
      }
      if (!name && this.searchTerms.boat_types) {
        name = this.$store?.getters['search/boatTypes']?.find(t => this.searchTerms.boat_types.includes(t.id))?.name
      }
      return capitalizeFirstLetter(name || this.$t('any-types'))
    },
    getBoatTypeId () {
      let name = ''
      if (this.filters?.boat_types && Array.isArray(this.filters?.boat_types)) {
        name = this.filters?.boat_types?.find(t => t.key === this.bar.current.boat_type)?.id
      }
      if (!name) {
        name = this.$store?.getters['search/boatTypes']?.find(t => t.id === this.bar.current.boat_type)?.id
      }
      return name
    },
    sortOptionsDsSource () {
      return this.$store.getters['search/sortOptionsDs'].map((item) => {
        return {
          ...item, localeTitle: this.$t(item.title)
        }
      })
    },
    getSearchResult () {
      return this.$store.state.search.results
    },
    hasSearchResult () {
      return !!this.getSearchResult?.length
    },
    getSearchCurrency () {
      if (this.results && this.results[0]) {
        return this.results[0].currency
      }
    },
    showChipComponent: app => (type) => {
      const terms = app.searchTerms
      if (type === 'boat_types' && terms.boat_type === 'any') {
        return false
      }
      return !!app.$Search.isChangedAttribute(type)
    },
    sortBy: {
      get () {
        return this.searchTerms.sortBy
      },
      set (value) {
        this.$Search.setAttributeValue('sortBy', value)
        this.$Search.resetAttributeValue('page')
        this.$nuxt.$emit('applyFilters')
      }
    },
    searchIsResettable () {
      return this.$Search.isResettable()
    },
    cabinsRange: {
      get () {
        const value = this.$Search.getAttributeValue('cabins')
        return [value.min, value.max]
      },
      set (array) {
        this.$Search.setAttributeValue('cabins', { min: array[0], max: array[1] })
      }
    },
    lengthRange: {
      get () {
        const value = this.$Search.getAttributeValue('length')
        if (value.min === value.max && value.min !== 0) {
          return [0, value.max]
        }
        return [value.min, value.max]
      },
      set (array) {
        this.$Search.setAttributeValue('length', { min: array[0], max: array[1] })
      }
    },
    yearsRange: {
      get () {
        const value = this.$Search.getAttributeValue('years')
        if (value.min === value.max && value.min !== 0) {
          return [0, value.max]
        }
        return [value.min, value.max]
      },
      set (array) {
        this.$Search.setAttributeValue('years', { min: array[0], max: array[1] })
      }
    },
    bathroomsRange: {
      get () {
        const value = this.$Search.getAttributeValue('bathrooms')
        return [value.min, value.max]
      },
      set (array) {
        this.$Search.setAttributeValue('bathrooms', { min: array[0], max: array[1] })
      }
    },
    guestsRange: {
      get () {
        const value = this.$Search.getAttributeValue('guestRange')
        return [value.min, value.max]
      },
      set (array) {
        this.$Search.setAttributeValue('guestRange', { min: array[0], max: array[1] })
      }
    },
    getAttributeRange () {
      return key => ({
        get: (index) => {
          const value = this.$Search.getAttributeValue(key)
          return [value.min, value.max][index]
        },
        set: (array) => {
          this.$Search.setAttributeValue(key, { min: array[0], max: array[1] })
        }
      })
    },
    getPriceOptions: app => (type) => {
      if (!app.getFilter?.prices?.[type]) {
        return []
      }
      return app.getFilter.prices[type].map((option) => {
        return {
          ...option,
          id: option.value && option.value !== '*' ? `${option.value}` : null,
          name: app.$options.filters.numberWithCommas(isNaN(parseFloat(option.label).toFixed(2)) ? option.label : parseFloat(option.label).toFixed(2))
        }
      })
    },
    getFilterRange: app => (key) => {
      return app.$Search.getFilterRange(key) || { min: 0, max: 0 }
    },
    hasFilterRange: app => (key) => {
      return app.$Search.hasFilterRange(key)
    },
    attributeInList: app => (key, value) => {
      return app.$Search.attributeInList(key, value)
    },
    searchLoading () {
      return this.$Search.loading
    },
    priceStartModel: {
      get () {
        const result = this.$Search.getAttributeValue('price_start')
        return result ? `${result}` : result
      },
      set (value) {
        this.$Search.setAttributeValue('price_start', value)
      }
    },
    priceEndModel: {
      get () {
        const result = this.$Search.getAttributeValue('price_end')
        return result ? `${result}` : result
      },
      set (value) {
        this.$Search.setAttributeValue('price_end', value)
      }
    }
  },
  methods: {
    getBoatTypeSlug (types) {
      let name = null
      name = this.$store?.getters['search/boatTypes']?.find(t => types.includes(t.id))?.slug
      return name
    },
    searchReset () {
      this.$Search.softResetAllAttributes()
      this.$Search.search()
    },
    getAttributeValueTitle (key, value) {
      return this.$Search.getAttributeValueTitle(key, value)
    },
    getAttributeValue (key) {
      return this.$Search.getAttributeValue(key)
    }
  }
}
